/**
 * お知らせバナー フック (steering 488)
 *
 * - 運営コンソール (Super Admin): 一覧 / 作成 / 更新
 * - テナント管理画面: 自組織向けの公開中バナー read-only
 *
 * `useFetch` / `useMutation` パターンに準拠する (§4.3。component から
 * `apiFetch` を直接呼ばない)。
 */
import { useCallback } from 'react';
import { apiFetch } from '@/api/client';
import { useFetch, useMutation } from '@/hooks/useApi';
import type {
  ActiveAnnouncement,
  Announcement,
  AnnouncementLevel,
  AnnouncementStatus,
  AnnouncementStyle,
  AnnouncementTarget,
} from '@/types';

/** 一覧 API のレスポンス。 */
export interface AnnouncementListResponse {
  items: Announcement[];
  total: number;
}

/** テナント配信 API のレスポンス。 */
export interface ActiveAnnouncementResponse {
  items: ActiveAnnouncement[];
}

/** 作成・更新の入力 (BE の validation と 1:1)。 */
export interface AnnouncementInput {
  title: string;
  message: string;
  level: AnnouncementLevel;
  style: AnnouncementStyle;
  target: AnnouncementTarget;
  startsAt: string;
  endsAt: string;
  enabled?: boolean;
  linkLabel?: string;
  linkUrl?: string;
}

// ==============================
// 運営コンソール (Super Admin)
// ==============================

/** お知らせ一覧を取得する。既定は公開中 (`active`)。 */
export function useAnnouncements(status: AnnouncementStatus = 'active') {
  return useFetch<AnnouncementListResponse>(`/platform/announcements?status=${status}`);
}

/** お知らせを新規作成する。 */
export function useCreateAnnouncement() {
  const fn = useCallback(
    (input: AnnouncementInput) =>
      apiFetch<Announcement>('/platform/announcements', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    [],
  );
  return useMutation<AnnouncementInput, Announcement>(fn);
}

/**
 * お知らせを更新する。
 *
 * 公開トグルと archive も同じ PUT の部分更新で扱う
 * (PATCH は API Gateway の登録 method に無く、採用すると CDK 変更になるため)。
 */
export function useUpdateAnnouncement() {
  const fn = useCallback(
    (
      input: { announcementId: string } & Partial<AnnouncementInput> & {
          status?: AnnouncementStatus;
        },
    ) => {
      const { announcementId, ...patch } = input;
      return apiFetch<Announcement>(`/platform/announcements/${announcementId}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
    },
    [],
  );
  return useMutation<
    { announcementId: string } & Partial<AnnouncementInput> & { status?: AnnouncementStatus },
    Announcement
  >(fn);
}

// ==============================
// テナント管理画面 (read-only)
// ==============================

/**
 * 自組織向けの公開中バナーを取得する。
 *
 * 対象組織は BE が JWT claim から決めるため、ここで orgId を渡さない
 * (渡しても BE は無視する)。`enabled` が false のときは fetch 自体を行わない。
 */
export function useActiveAnnouncements(enabled: boolean) {
  return useFetch<ActiveAnnouncementResponse>(enabled ? '/platform/announcements/active' : null);
}
